const GA_MEASUREMENT_ID = 'G-EKX8X6G8ML';

type GtagCommand = [
  'config' | 'consent' | 'event' | 'js' | 'set',
  string | Date,
  Record<string, unknown>?
];

declare global {
  interface Window {
    dataLayer?: GtagCommand[];
    gtag?: (...args: GtagCommand) => void;
    [key: string]: unknown;
  }
}

let isAnalyticsLoaded = false;

const setAnalyticsDisabled = (disabled: boolean): void => {
  window[`ga-disable-${GA_MEASUREMENT_ID}`] = disabled;
};

const ensureGtag = (): void => {
  if (!window.dataLayer) {
    window.dataLayer = [];
  }

  if (!window.gtag) {
    window.gtag = (...args: GtagCommand) => {
      window.dataLayer?.push(args);
    };
  }
};

const loadGtagScript = (): Promise<void> => {
  if (document.getElementById('ga4-script')) {
    return Promise.resolve();
  }

  return new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.id = 'ga4-script';
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error('Failed to load Google Analytics'));
    document.head.appendChild(script);
  });
};

const initAnalytics = (): void => {
  ensureGtag();
  window.gtag?.('js', new Date());
  window.gtag?.('config', GA_MEASUREMENT_ID, {
    anonymize_ip: true,
  });
  isAnalyticsLoaded = true;
};

const clearAnalyticsCookies = (): void => {
  const cookies = document.cookie.split(';');
  cookies.forEach((cookie) => {
    const [rawName] = cookie.split('=');
    const name = rawName?.trim();
    if (name && name.startsWith('_ga')) {
      document.cookie = `${name}=; Max-Age=0; path=/; SameSite=Lax`;
    }
  });
};

export const enableAnalytics = async (): Promise<void> => {
  setAnalyticsDisabled(false);

  if (isAnalyticsLoaded) {
    window.gtag?.('config', GA_MEASUREMENT_ID, {
      anonymize_ip: true,
    });
    return;
  }

  await loadGtagScript();
  initAnalytics();
};

export const disableAnalytics = (): void => {
  setAnalyticsDisabled(true);
  clearAnalyticsCookies();
};